import { Helmet } from "react-helmet-async";
import { productOffers } from "../utils/productStructuredData";

const SITE_NAME = "일광오피스";
const BASE_URL = "https://ikoffices.com";

const defaults = {
  title: "복사기 복합기 렌탈·판매 전문 | 일광오피스 - 가격비교·토너·무상유지보수",
  description:
    "일광오피스에서 사무용 복사기·복합기 렌탈과 구매 가격, 프린터·토너 소모품을 비교하세요. 출력량과 설치 지역에 맞는 제품, 유지보수와 계약 조건을 상담해 드립니다.",
  keywords:
    "복사기, 복합기, 복사기 렌탈, 복합기 렌탈, 사무용 복사기, 복사기 가격, 복합기 가격, 복사기 추천, 복합기 추천, 복사기 판매, 사무용 복합기, 컬러 복사기, 흑백 복사기, 토너, 프린터, 복사기 렌탈 가격, 복합기 렌탈 가격, 복사기 유지보수, 사무실 복사기, 일광오피스",
};

export const normalizeImageUrl = (value) => {
  // Reject control characters before URL parsing can silently normalize them.
  // eslint-disable-next-line no-control-regex
  if (typeof value !== "string" || /[\u0000-\u001f\u007f\\]/.test(value)) return "";
  const candidate = value.trim();
  if (!candidate || !(candidate.startsWith("/") || /^https?:\/\//i.test(candidate))) return "";
  try {
    const url = new URL(candidate.startsWith("//") ? `https:${candidate}` : candidate, BASE_URL);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) return "";
    url.hash = "";
    return url.href;
  } catch {
    return "";
  }
};

export const normalizeProductImages = (images, fallbackAlt = "상품 이미지") => {
  const seen = new Set();
  return (Array.isArray(images) ? images : []).reduce((result, image) => {
    const url = normalizeImageUrl(typeof image === "string" ? image : image?.url);
    if (!url || seen.has(url) || result.length >= 12) return result;
    seen.add(url);
    const suppliedAlt = typeof image?.alt === "string" ? image.alt : fallbackAlt;
    // eslint-disable-next-line no-control-regex
    const alt = String(suppliedAlt || fallbackAlt).replace(/<[^>]*>/g, "").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
    result.push({ url, alt: alt || "상품 이미지" });
    return result;
  }, []);
};

export default function SEO({ title, description, keywords, path = "", noindex = false, product = null, breadcrumbs = null, areaServed = "" }) {
  const pageTitle = title || defaults.title;
  const pageDesc = description || defaults.description;
  const pageKeywords = keywords || defaults.keywords;
  const canonical = `${BASE_URL}${path || "/"}`;
  const suppliedImages = normalizeProductImages(product?.images, product?.name);
  const productImages = suppliedImages.length ? suppliedImages
    : normalizeProductImages([{ url: product?.image, alt: product?.imageAlt || product?.name }]);
  const shareImage = productImages[0]?.url || `${BASE_URL}/og-image.png`;
  const shareImageAlt = productImages[0]?.alt || SITE_NAME;
  const offers = product ? productOffers(product, canonical) : [];

  /* ── JSON-LD Product schema for Google/Naver Shopping ── */
  const productJsonLd = product ? {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: pageDesc,
    image: productImages.length ? productImages.map(image => image.url) : [shareImage],
    ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
    sku: product.sku || `IK-${product.id || "0"}`,
    ...(product.model ? { model: product.model, mpn: product.model } : {}),
    url: canonical,
    category: product.category || "복사기",
    ...(offers.length ? { offers: offers.length === 1 ? offers[0] : offers } : {}),
    ...(product.specs ? { additionalProperty: Object.entries(product.specs).slice(0, 10).map(([k, v]) => ({ "@type": "PropertyValue", name: k, value: v })) } : {}),
  } : null;

  /* ── Breadcrumb JSON-LD ── */
  const breadcrumbJsonLd = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: b.url ? `${BASE_URL}${b.url}` : undefined,
    })),
  } : null;

  /* ── Service + LocalBusiness JSON-LD (지역 렌탈 — areaServed 로컬 신호) ── */
  const serviceJsonLd = areaServed ? {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "복합기·복사기 렌탈",
    name: pageTitle,
    description: pageDesc,
    url: canonical,
    areaServed: { "@type": "AdministrativeArea", name: areaServed },
    provider: {
      "@type": "LocalBusiness",
      name: SITE_NAME,
      telephone: "1600-7542",
      url: BASE_URL,
      priceRange: "₩₩",
      areaServed: { "@type": "AdministrativeArea", name: areaServed },
    },
  } : null;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />
      <meta name="googlebot" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large"} />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ko_KR" />
      {product ? <meta property="og:type" content="product" /> : <meta property="og:type" content="website" />}
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:alt" content={shareImageAlt} />
      {offers.length > 0 && <meta property="product:price:amount" content={String(offers[0].price)} />}
      {offers.length > 0 && <meta property="product:price:currency" content="KRW" />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={shareImage} />
      <meta name="twitter:image:alt" content={shareImageAlt} />

      {/* JSON-LD Product structured data */}
      {productJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd)}
        </script>
      )}

      {/* JSON-LD Breadcrumbs */}
      {breadcrumbJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      )}

      {/* JSON-LD Service (지역 렌탈 areaServed 로컬 신호) */}
      {serviceJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
      )}
    </Helmet>
  );
}
